import { useRef, useEffect, useMemo } from "react";
import {
  ChallengeCampaign,
  ChallengeCampaignEventState,
  CompleteTaskMutation,
  TaskStatus,
} from "@/types";
import { getInstancesTimeframe, getMaxPoints } from "@/lib/challenges";
import { BadgeCheckIcon, ChevronRightIcon, ClockIcon, Zap } from "lucide-react";
import { TaskTypes } from "@/config/task-types";
import { DefaultTaskRenderer } from "./task-renderers";
import { IAirdropTaskRender } from "./task-renderers/task-renderer.types";

import { TwitterLinkNotice } from "./task-renderers/components/twitter-link-notice";
import { TelegramLinkNotice } from "./task-renderers/components/telegram-link-notice";
import { TwitterSessionExpiredNotice } from "./task-renderers/components/twitter-session-expired-notice";
import { DiscordSessionExpiredNotice } from "./task-renderers/components/discord-session-expired-notice";
import { AuthenticationNotice } from "./task-renderers/components/authentication-notice";
import { TaskLoading } from "./task-renderers/components/task-loading";
import { DiscordLinkNotice } from "./task-renderers/components/discord-link-notice";
import { LinkedInLinkNotice } from "./task-renderers/components/linkedin-link-notice";
import { LinkedInSessionExpiredNotice } from "./task-renderers/components/linkedin-session-expired-notice";
import { MediaRenderer } from "./task-renderers/media-renderer";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { useAuth } from "@/hooks/useAuth";
import { TELEGRAM_BOT_NAME } from "@/config/constants";
import { FeatureCard } from "@/components";
import { QuizRenderer } from "./task-renderers/quiz-renderer";
import { ClickLinkRenderer } from "./task-renderers/click-link-renderer";
import { RetweetRenderer } from "./task-renderers/retweet-renderer";
import { SecretCodeRenderer } from "./task-renderers/secret-code-renderer";
import { TwitterUsernameRenderer } from "./task-renderers/twitter-username-renderer";
import { FarmerWarningBanner } from "./task-renderers/components/farmer-warning-banner";
import { OtcPointsBanner } from "./task-renderers/components/otc-points-banner";

interface QuestTaskProps {
  task: TaskStatus;
  tokenLogo: string;
  completeTaskMutation: CompleteTaskMutation;
  campaign: ChallengeCampaign;
  openedTaskGuid: string | null;
  setOpenedTaskGuid: (guid: string | null) => void;
  onNextTask: () => void;
  onPreviousTask: () => void;
  hasNextTask: boolean;
  hasPreviousTask: boolean;
}

const TWITTER_TASKS: string[] = [
  TaskTypes.FOLLOW_TWITTER,
  TaskTypes.RETWEET,
  TaskTypes.RETWEET_MVX,
  TaskTypes.TWITTER_USERNAME,
  TaskTypes.TWEET_KEYWORDS,
  TaskTypes.LIKE_POST,
];
const TELEGRAM_TASKS: string[] = [TaskTypes.JOIN_TELEGRAM];
const DISCORD_TASKS: string[] = [
  TaskTypes.JOIN_DISCORD,
  TaskTypes.DISCORD_ROLE,
];
const LINKEDIN_TASKS: string[] = [
  TaskTypes.JOIN_LINKEDIN,
  TaskTypes.FOLLOW_LINKEDIN,
];

export const QuestRenderer = ({
  task,
  campaign,
  address,
  challengesUser,
  completeTaskMutation,
}: IAirdropTaskRender) => {
  const { data: socialUser, isLoading: isSocialUserLoading } =
    useGetSocialUser();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const { linkTwitter, linkTelegram, linkDiscord, linkLinkedIn } =
    useSocialPayMutations();

  const isInitialLoading =
    isAuthLoading ||
    (isAuthenticated &&
      TWITTER_TASKS.includes(task.taskName) &&
      task.taskName !== TaskTypes.TWITTER_USERNAME &&
      isSocialUserLoading);

  // Show loading state during initial data fetch
  if (isInitialLoading) {
    return <TaskLoading message="Loading your data..." />;
  }

  // Check if user is authenticated
  // This check should come first since other checks depend on authenticated API calls
  if (!isAuthenticated) {
    return <AuthenticationNotice />;
  }

  // Check if user is a farmer
  const isWhitelisted = challengesUser?.isFarmerWhitelisted ?? false;
  const isFarmer = (challengesUser?.scamInfo?.length ?? 0) > 0;

  if (isFarmer && !isWhitelisted) {
    return <FarmerWarningBanner />;
  }

  // Check if social auth is needed
  const needsTwitterAuth =
    TWITTER_TASKS.includes(task.taskName) &&
    task.taskName !== TaskTypes.TWITTER_USERNAME &&
    !socialUser?.twitterMetadata?.username;
  const needsTelegramAuth =
    TELEGRAM_TASKS.includes(task.taskName) &&
    !socialUser?.telegramMetadata?.username;
  const needsDiscordAuth =
    DISCORD_TASKS.includes(task.taskName) &&
    !socialUser?.discordMetadata?.username;
  const needsLinkedInAuth =
    LINKEDIN_TASKS.includes(task.taskName) &&
    !socialUser?.linkedInMetadata?.name;

  const needsTwitterReconnect =
    TWITTER_TASKS.includes(task.taskName) &&
    task.taskName !== TaskTypes.TWITTER_USERNAME &&
    !(
      socialUser?.twitterMetadata?.encryptedAccessToken ||
      socialUser?.twitterMetadata?.encryptedRefreshToken
    );

  const needsDiscordReconnect =
    DISCORD_TASKS.includes(task.taskName) &&
    socialUser?.discordMetadata?.username &&
    !(
      socialUser?.discordMetadata?.encryptedAccessToken ||
      socialUser?.discordMetadata?.encryptedRefreshToken
    );

  const needsLinkedInReconnect =
    LINKEDIN_TASKS.includes(task.taskName) &&
    socialUser?.linkedInMetadata?.name &&
    !socialUser?.linkedInMetadata?.encryptedAccessToken;

  if (needsTwitterAuth) {
    return (
      <TwitterLinkNotice
        onConnect={(redirectUrl) => {
          linkTwitter?.mutate({
            redirectUrl,
          });
        }}
      />
    );
  }

  if (needsTelegramAuth) {
    return (
      <TelegramLinkNotice
        botName={TELEGRAM_BOT_NAME}
        onAuth={(user) => {
          linkTelegram?.mutate({
            ...user,
          });
        }}
      />
    );
  }

  if (needsDiscordAuth) {
    return (
      <DiscordLinkNotice
        onConnect={() => {
          linkDiscord?.mutate();
        }}
      />
    );
  }

  if (needsLinkedInAuth) {
    return (
      <LinkedInLinkNotice
        onConnect={() => {
          linkLinkedIn?.mutate();
        }}
      />
    );
  }

  if (needsTwitterReconnect && socialUser?.twitterMetadata) {
    return (
      <TwitterSessionExpiredNotice
        onReconnect={() => {
          const redirectUrl = window.location.href;
          linkTwitter?.mutate({
            redirectUrl,
          });
        }}
        twitterMetadata={socialUser?.twitterMetadata}
      />
    );
  }

  if (needsDiscordReconnect && socialUser?.discordMetadata) {
    return (
      <DiscordSessionExpiredNotice
        onReconnect={() => {
          linkDiscord?.mutate();
        }}
        discordMetadata={socialUser?.discordMetadata}
      />
    );
  }

  if (needsLinkedInReconnect && socialUser?.linkedInMetadata) {
    return (
      <LinkedInSessionExpiredNotice
        onReconnect={() => {
          linkLinkedIn?.mutate();
        }}
        linkedInMetadata={socialUser?.linkedInMetadata}
      />
    );
  }

  // Rest of your existing switch statement...
  const rendererProps = {
    task,
    completeTaskMutation,
    campaign,
    address,
    challengesUser,
  };

  // Regular rendering without email info
  return renderTaskContent(task.taskName, rendererProps);
};

// Helper function to render the appropriate task content
function renderTaskContent(
  taskName: string,
  rendererProps: IAirdropTaskRender
) {
  switch (taskName) {
    // Secret Code
    case "first_points_on_us":
      // hidden
      return null;
    case TaskTypes.SECRET_CODE:
      return <SecretCodeRenderer {...rendererProps} />;

    // Twitter Tasks
    case TaskTypes.RETWEET:
    case TaskTypes.RETWEET_MVX:
      return <RetweetRenderer {...rendererProps} />;

    case TaskTypes.TWITTER_USERNAME:
      return <TwitterUsernameRenderer {...rendererProps} />;

    // Quiz Tasks
    case TaskTypes.QUIZ:
      return <QuizRenderer {...rendererProps} />;

    // Media Tasks
    case TaskTypes.CREATE_MEDIA:
    case TaskTypes.TWEET_KEYWORDS:
      return <MediaRenderer {...rendererProps} />;

    // Link Tasks
    case TaskTypes.CLICK_LINK:
    case TaskTypes.SHARE_PROGRESS:
      return <ClickLinkRenderer {...rendererProps} />;

    // OTC Points - weekly distribution, no claim button
    case TaskTypes.OTC_POINTS:
      return <OtcPointsBanner />;

    // All other tasks use default renderer (includes social tasks, email tasks, etc.)
    default:
      return <DefaultTaskRenderer {...rendererProps} />;
  }
}

export const QuestTask = ({
  task,
  campaign,
  openedTaskGuid,
  setOpenedTaskGuid,
}: QuestTaskProps) => {
  const taskRef = useRef<HTMLButtonElement>(null);
  const isTaskCompleted =
    task.status === ChallengeCampaignEventState.SUCCESSFUL;

  useEffect(() => {
    if (window.location.hash === `#${task.taskGuid}`) {
      taskRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setOpenedTaskGuid(task.taskGuid);
    }
  }, [task.taskGuid, setOpenedTaskGuid]);

  const challengeTask = useMemo(() => {
    return campaign.tasks?.find((t) => t.id === task.taskGuid);
  }, [task.taskGuid, campaign.tasks]);

  const handleToggle = () => {
    if (openedTaskGuid === task.taskGuid) {
      setOpenedTaskGuid(null);
    } else {
      setOpenedTaskGuid(task.taskGuid);
    }
  };

  const args = campaign.tasks?.find((t) => t.id === task.taskGuid)?.arguments;
  const tiers = args?.find((arg) => arg.name === "pointsTiers");
  const isDynamic = !!tiers || task.taskGuid === TaskTypes.CREATE_MEDIA;

  const timeframe = useMemo(() => {
    if (!challengeTask) return null;
    return getInstancesTimeframe(challengeTask);
  }, [challengeTask]);

  return (
    <button
      ref={taskRef}
      id={task.taskGuid}
      onClick={handleToggle}
      className="w-full text-left"
      aria-expanded={openedTaskGuid === task.taskGuid}
      aria-controls={`drawer-${task.taskGuid}`}
      aria-label={`${task.title} - ${
        isTaskCompleted ? "Completed" : "Incomplete"
      } task`}
    >
      <FeatureCard
        key={task.taskGuid}
        className="p-4 relative cursor-pointer h-full hover:-translate-y-1 transition-all duration-200 ease-out"
      >
        {isTaskCompleted ? (
          <BadgeCheckIcon className="absolute top-1/2 -translate-y-1/2 right-4 text-swapper-success w-6 h-6" />
        ) : task.status === ChallengeCampaignEventState.PENDING ? (
          <ClockIcon className="absolute top-1/2 -translate-y-1/2 right-4 text-yellow-500 w-6 h-6" />
        ) : (
          <ChevronRightIcon className="absolute top-1/2 -translate-y-1/2 right-4 text-gray-400 w-6 h-6" />
        )}
        <div className="flex items-center gap-4 pr-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-swapper-primary to-[#9F7FFF] rounded-xl flex items-center justify-center shadow-md">
            {task.media.darkModeImageUrl ? (
              <img
                width={40}
                height={40}
                src={task.media.darkModeImageUrl}
                alt="Task Icon"
                className="object-contain filter brightness-0 invert opacity-90"
              />
            ) : (
              <Zap className="w-8 h-8 text-white" />
            )}
          </div>
          <div className="flex flex-col justify-between gap-2">
            <p className="text-sm sm:text-base font-semibold text-swapper-secondary">{task.title}</p>
            <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
              {isDynamic && (
                <span className="text-xs text-swapper-secondary/60">Up to</span>
              )}
              <div className="flex items-center gap-2">
                <p className="font-bold text-swapper-primary">
                  {challengeTask && getMaxPoints(challengeTask)}
                </p>
                <Zap className="w-4 h-4 text-swapper-primary" />
              </div>
              {timeframe && (
                <span className="text-sm text-swapper-secondary/60">/ {timeframe}</span>
              )}
            </div>
          </div>
        </div>
      </FeatureCard>
    </button>
  );
};
